import type * as User from 'ab-viewModels/user.viewModel';

import * as TextUtil from '@acceligentllc/shared/utils/text';

import type { LoginMethod } from '@acceligentllc/shared/enums/loginMethod';

import { LOCALSTORAGE_MULTIPLE_SIGNINS } from 'af-constants/localstorage';

export class SignInData implements User.SignInData {
	orgAlias: string;
	userId: number;
	token: string;
	email: Nullable<string>;
	isPlatformAdmin: boolean;
	isFinalized: boolean;
	isEmailFinalized: boolean;
	loginMethod: LoginMethod;
	/** Tracks active company - only avaliable on FE */
	companyId?: number;
	/** name from URL */
	companyName?: string;

	constructor(
		orgAlias: string,
		userId: number,
		token: string,
		isPlatformAdmin: boolean,
		isFinalized: boolean,
		isEmailFinalized: boolean,
		email: Nullable<string>,
		loginMethod: LoginMethod,
		companyId?: number,
		companyName?: string
	) {
		this.orgAlias = orgAlias;
		this.userId = userId;
		this.token = token;
		this.isPlatformAdmin = isPlatformAdmin;
		this.isFinalized = isFinalized;
		this.isEmailFinalized = isEmailFinalized;
		this.email = email;
		this.companyId = companyId;
		this.companyName = companyName;
		this.loginMethod = loginMethod;
	}
}

const _isAdminSingIn = (signIn: SignInData) => signIn.isPlatformAdmin;

export function getAllSignIns(): SignInData[] {
	const allSignInsString: Nullable<string> = localStorage.getItem(LOCALSTORAGE_MULTIPLE_SIGNINS);
	return allSignInsString ? JSON.parse(allSignInsString) : [];
}

export function getSignInForOrg(orgAlias: Nullable<string>, fallbackToAdmin: boolean = false): Nullable<SignInData> {
	const allSignIns: SignInData[] = getAllSignIns();

	let signIn = allSignIns.find((_signIn: SignInData) => TextUtil.isLowerCaseEqual(_signIn.orgAlias, orgAlias));

	if (!signIn && fallbackToAdmin) {
		signIn = allSignIns.find(_isAdminSingIn);
	}
	return signIn ?? null;
}

export function updateSignInForOrg(orgAlias: string, signInData: Partial<SignInData>, returnFalseIfDoesNotExist: boolean = false): boolean {
	const allSignIns: SignInData[] = getAllSignIns();

	const existingSignInIndex = allSignIns.findIndex((_signIn: SignInData) => TextUtil.isLowerCaseEqual(_signIn.orgAlias, orgAlias));
	if (existingSignInIndex === -1) {
		if (returnFalseIfDoesNotExist) {
			return false;
		}
		throw new Error('No sign in data for organization to update');
	}
	const data: SignInData = { ...allSignIns[existingSignInIndex], ...signInData };
	allSignIns.splice(existingSignInIndex, 1, data);

	localStorage.setItem(LOCALSTORAGE_MULTIPLE_SIGNINS, JSON.stringify(allSignIns));
	return true;
}

export function upsertSignInForOrg(orgAlias: string, signInData: SignInData) {
	const allSignIns: SignInData[] = getAllSignIns();

	const existingSignInIndex = allSignIns.findIndex((_signIn: SignInData) => TextUtil.isLowerCaseEqual(_signIn.orgAlias, orgAlias));
	if (existingSignInIndex === -1) {
		allSignIns.push(signInData);
	} else {
		const data: SignInData = { ...allSignIns[existingSignInIndex], ...signInData };
		allSignIns.splice(existingSignInIndex, 1, data);
	}

	localStorage.setItem(LOCALSTORAGE_MULTIPLE_SIGNINS, JSON.stringify(allSignIns));
}

export function removeSignInForOrg(orgAlias: string) {
	const allSignIns: SignInData[] = getAllSignIns();

	const index: number = allSignIns.findIndex((_signIn: SignInData) => TextUtil.isLowerCaseEqual(_signIn.orgAlias, orgAlias));
	if (index >= 0) {
		allSignIns.splice(index, 1);
	}
	localStorage.setItem(LOCALSTORAGE_MULTIPLE_SIGNINS, JSON.stringify(allSignIns));
}

export function getPlatformAdminDataFromStorage(): SignInData | undefined {
	const allSignIns: SignInData[] = getAllSignIns();
	return allSignIns.find(_isAdminSingIn);
}

export function addPlatformAdminDataToStorage(data: SignInData): void {
	if (!data) {
		return;
	}
	const allSignIns: SignInData[] = getAllSignIns();

	const adminSignInIndex: number = allSignIns.findIndex(_isAdminSingIn);

	if (adminSignInIndex < 0) {
		allSignIns.push(data);
	} else {
		allSignIns.splice(adminSignInIndex, 1, data);
	}

	localStorage.setItem(LOCALSTORAGE_MULTIPLE_SIGNINS, JSON.stringify(allSignIns));
}
